/**
 * 20/10/2026 — a route test's winner ranking out of the box: Delivered Rate
 * on its own drops today, "Not enough data" = nothing sent. The generic
 * gateway default (CR over 30 days, 100 unique visits) can never pick a
 * winner on a handful of test SMS — the Belgium real test showed it. A
 * stored ranking is read as it is; an untouched generic default becomes
 * this one.
 * For Media Devoted | Created By Ciro Autiero
 */
import { defaultGatewayRanking, parseGatewayRanking } from "../gateway-ranking/gateway-ranking";
import type { DropGenGatewayRanking } from "../gateway-ranking/gateway-ranking.types";
import { defaultHourFormula } from "../hour-formula";
import { DROP_GEN_EXCLUDE_ACTION, DROP_GEN_FIRST_RANGE } from "../rules/rules.types";
import { NOT_ENOUGH_DATA_ID } from "../rules/default-rules";
import { lockRouteTestRanking } from "./route-test";

export const ROUTE_TEST_DEFAULT_SETTLE_MIN = 30;

/** Delivered Rate today, "Not enough data" = no SMS sent, judged on the test's own drops. */
export function defaultRouteTestRanking(): DropGenGatewayRanking {
  const formula = defaultHourFormula("today", "deliveredRate");
  const id = formula.groups[0].id;
  formula.groups[0] = { ...formula.groups[0], id: "gateways-1", name: "Gateways" };
  formula.groupWeights = { "gateways-1": formula.groupWeights[id] ?? 100 };
  return {
    formula,
    rules: [
      {
        id: NOT_ENOUGH_DATA_ID,
        name: "Not enough data",
        enabled: true,
        dateRange: DROP_GEN_FIRST_RANGE,
        when: { kind: "group", op: "AND", children: [{ kind: "condition", metric: "smsSent", op: "<", value: 1 }] },
        action: DROP_GEN_EXCLUDE_ACTION,
      },
    ],
    source: "drops",
    routeTestSettleMin: ROUTE_TEST_DEFAULT_SETTLE_MIN,
  };
}

/** The generic gateway default, untouched — what a new route test's draft starts with. */
export const isDefaultGatewayRanking = (ranking: DropGenGatewayRanking): boolean =>
  JSON.stringify({ formula: ranking.formula, rules: ranking.rules }) === JSON.stringify(defaultGatewayRanking()) && !ranking.source;

/** A route test's ranking as the editor and the engine read it: stored → locked on today; untouched → the delivery default. */
export function routeTestRankingOf(ranking: DropGenGatewayRanking): DropGenGatewayRanking {
  return isDefaultGatewayRanking(ranking) ? defaultRouteTestRanking() : lockRouteTestRanking(ranking);
}

/** A stored route-test ranking (the gateway satellite's `orderFormulaJson`); null or the generic default → the delivery default. */
export const parseRouteTestRanking = (json: unknown): DropGenGatewayRanking => routeTestRankingOf(parseGatewayRanking(json));
